import { useState, useEffect, useCallback } from 'react';
import jwt_decode from 'jwt-decode';
import { toast } from 'react-toastify';
import axios from '../api/axios';

const API_BASE = '/v2/retell-ai';

const getAuthHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('accessToken')}` });

const api = {
    get: (url, params = {}) =>
        axios.get(`${API_BASE}${url}`, { params, headers: getAuthHeader() }),
    patch: (url, data) =>
        axios.patch(`${API_BASE}${url}`, data, { headers: getAuthHeader() }),
    delete: (url) =>
        axios.delete(`${API_BASE}${url}`, { headers: getAuthHeader() }),
};

const STATUS_COLORS = {
    completed: { bg: '#d1fae5', text: '#065f46', dot: '#10b981' },
    missed: { bg: '#fee2e2', text: '#991b1b', dot: '#ef4444' },
    failed: { bg: '#fef3c7', text: '#92400e', dot: '#f59e0b' },
    voicemail: { bg: '#ede9fe', text: '#5b21b6', dot: '#8b5cf6' },
    busy: { bg: '#fce7f3', text: '#9d174d', dot: '#ec4899' },
    no_answer: { bg: '#f3f4f6', text: '#374151', dot: '#9ca3af' },
    in_progress: { bg: '#dbeafe', text: '#1e40af', dot: '#3b82f6' },
};

const OUTCOME_COLORS = {
    appointment_booked: { bg: '#d1fae5', text: '#065f46' },
    sms_sent: { bg: '#dbeafe', text: '#1e40af' },
    info_provided: { bg: '#e0e7ff', text: '#3730a3' },
    not_interested: { bg: '#fee2e2', text: '#991b1b' },
    voicemail: { bg: '#ede9fe', text: '#5b21b6' },
    unknown: { bg: '#f3f4f6', text: '#6b7280' },
};

const SENTIMENT_COLORS = {
    positive: { bg: '#d1fae5', text: '#065f46' },
    neutral: { bg: '#f3f4f6', text: '#374151' },
    negative: { bg: '#fee2e2', text: '#991b1b' },
};

function toTitleCase(str) {
    if (!str) return '';
    return str
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
}

function Badge({ label, colors }) {
    if (!label) return null;
    const c = colors?.[label] || { bg: '#f3f4f6', text: '#374151' };
    return (
        <span style={{ background: c.bg, color: c.text, padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
            {toTitleCase(label)}
        </span>
    );
}

function StatusDot({ status }) {
    const c = STATUS_COLORS[status || ''] || { dot: '#9ca3af' };
    return <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: c.dot, marginRight: 6, flexShrink: 0 }} />;
}

function formatDuration(ms) {
    if (!ms) return '—';
    const s = Math.round(ms / 1000);
    if (s < 60) return `${s}s`;
    return `${Math.floor(s / 60)}m ${s % 60}s`;
}

function formatDate(dateStr) {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('en-CA', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatDateShort(dateStr) {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' });
}

function PhoneIcon() {
    return <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.68A2 2 0 012 .82h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" /></svg>;
}

function SearchIcon() {
    return <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>;
}

function FilterIcon() {
    return <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>;
}

function RefreshIcon() {
    return <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" /></svg>;
}

function ArrowIcon({ dir = 'right' }) {
    return <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ transform: dir === 'left' ? 'rotate(180deg)' : 'none' }}><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>;
}

function XIcon() {
    return <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>;
}

function CalendarIcon() {
    return <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>;
}

function MicIcon() {
    return <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" /><path d="M19 10v2a7 7 0 01-14 0v-2" /><line x1="12" y1="19" x2="12" y2="23" /><line x1="8" y1="23" x2="16" y2="23" /></svg>;
}

function UserIcon() {
    return <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>;
}

function HomeIcon() {
    return <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg>;
}

function MessageIcon() {
    return <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" /></svg>;
}

function ClockIcon() {
    return <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>;
}

function Pagination({ pagination, onPageChange }) {
    if (!pagination || pagination.pages <= 1) return null;
    const { page, pages } = pagination;
    return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 0', borderTop: '1px solid #f1f5f9' }}>
            <span style={{ fontSize: 13, color: '#64748b' }}>
                Page {page} of {pages} &nbsp;·&nbsp; {pagination.total} Total
            </span>
            <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => onPageChange(page - 1)} disabled={page <= 1} style={{ ...btnStyle, opacity: page <= 1 ? 0.4 : 1 }}>
                    <ArrowIcon dir="left" />
                </button>
                {Array.from({ length: Math.min(5, pages) }, (_, i) => {
                    let p;
                    if (pages <= 5) p = i + 1;
                    else if (page <= 3) p = i + 1;
                    else if (page >= pages - 2) p = pages - 4 + i;
                    else p = page - 2 + i;
                    return (
                        <button key={p} onClick={() => onPageChange(p)} style={{ ...btnStyle, background: p === page ? '#1e293b' : '#f8fafc', color: p === page ? '#fff' : '#374151', borderColor: p === page ? '#1e293b' : '#e2e8f0', minWidth: 36 }}>
                            {p}
                        </button>
                    );
                })}
                <button onClick={() => onPageChange(page + 1)} disabled={page >= pages} style={{ ...btnStyle, opacity: page >= pages ? 0.4 : 1 }}>
                    <ArrowIcon dir="right" />
                </button>
            </div>
        </div>
    );
}

const btnStyle = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '7px 14px', border: '1px solid #e2e8f0', borderRadius: 8, background: '#f8fafc', color: '#374151', fontSize: 13, fontWeight: 500, cursor: 'pointer', transition: 'all 0.15s',
};

const inputStyle = {
    width: '100%', padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, color: '#1e293b', background: '#fff', outline: 'none', boxSizing: 'border-box',
};

const selectStyle = {
    ...inputStyle, cursor: 'pointer', appearance: 'none', backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center', paddingRight: 28,
};

const cardStyle = {
    background: '#fff', borderRadius: 14, border: '1px solid #f1f5f9', boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
};

function StatCard({ label, value, sub, accent }) {
    return (
        <div style={{ ...cardStyle, padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.8 }}>{label}</span>
            <span style={{ fontSize: 28, fontWeight: 800, color: accent || '#1e293b', lineHeight: 1.2 }}>{value ?? '—'}</span>
            {sub && <span style={{ fontSize: 12, color: '#64748b' }}>{sub}</span>}
        </div>
    );
}

function TranscriptViewer({ transcript }) {
    if (!transcript || transcript.length === 0) return <p style={{ color: '#94a3b8', fontSize: 13 }}>No Transcript Available.</p>;
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {transcript.map((turn, i) => (
                <div key={i} style={{ display: 'flex', gap: 10, flexDirection: turn.role === 'agent' ? 'row' : 'row-reverse', alignItems: 'flex-start' }}>
                    <div style={{ width: 30, height: 30, borderRadius: '50%', background: turn.role === 'agent' ? '#e0e7ff' : '#d1fae5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 11, fontWeight: 700, color: turn.role === 'agent' ? '#3730a3' : '#065f46' }}>
                        {turn.role === 'agent' ? 'AI' : 'U'}
                    </div>
                    <div style={{ maxWidth: '75%', background: turn.role === 'agent' ? '#f8faff' : '#f0fdf4', border: `1px solid ${turn.role === 'agent' ? '#e0e7ff' : '#d1fae5'}`, borderRadius: turn.role === 'agent' ? '4px 14px 14px 14px' : '14px 4px 14px 14px', padding: '8px 12px', fontSize: 13, color: '#1e293b', lineHeight: 1.5 }}>
                        {turn.text || turn.content}
                    </div>
                </div>
            ))}
        </div>
    );
}

function LeadDetailModal({ lead, onClose, onUpdate }) {
    const [callLog, setCallLog] = useState(null);
    const [chatLog, setChatLog] = useState(null);
    const [loadingLog, setLoadingLog] = useState(false);
    const [activeTab, setActiveTab] = useState('details');
    const [editNotes, setEditNotes] = useState(lead?.notes || '');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const callId = lead?.retell?.call_id;
        const chatId = lead?.retell?.chat_id;
        if (!callId && !chatId) {
            setCallLog(null);
            setChatLog(null);
            return;
        }
        setLoadingLog(true);
        const fetches = [];
        if (callId) fetches.push(api.get(`/call-logs/by-call/${callId}`).then(r => setCallLog(r.data.data)).catch(() => setCallLog(null)));
        else setCallLog(null);
        if (chatId) fetches.push(api.get(`/chat-logs/by-chat/${chatId}`).then(r => setChatLog(r.data.data)).catch(() => setChatLog(null)));
        else setChatLog(null);
        Promise.all(fetches).finally(() => setLoadingLog(false));
    }, [lead?.retell?.call_id, lead?.retell?.chat_id]);

    const transcriptTurns =
        callLog?.transcript?.length
            ? callLog.transcript
            : chatLog?.transcript?.length
                ? chatLog.transcript
                : lead?.transcription?.transcript || [];
    const summaryText = lead?.ai_summary || callLog?.call_summary || chatLog?.chat_summary || '';
    const sourceLog = callLog || chatLog;

    const handleSaveNotes = async () => {
        try {
            setSaving(true);
            await api.patch(`/leads/${lead._id}`, { notes: editNotes });
            toast.success('Notes Saved');
            onUpdate && onUpdate();
        } catch {
            toast.error('Failed To Save Notes');
        } finally {
            setSaving(false);
        }
    };

    if (!lead) return null;

    const tabs = [
        { id: 'details', label: 'Lead Details' },
        { id: 'transcript', label: 'Transcript' },
        { id: 'summary', label: 'AI Summary' },
    ];

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(4px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={e => e.target === e.currentTarget && onClose()}>
            <div style={{ background: '#fff', borderRadius: 18, width: '100%', maxWidth: 680, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 50px rgba(0,0,0,0.2)', overflow: 'hidden' }}>
                <div style={{ padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fafafa' }}>
                    <div>
                        <div style={{ fontWeight: 700, fontSize: 16, color: '#1e293b' }}>{toTitleCase(lead.name) || lead.phone || 'Unknown Caller'}</div>
                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{lead.phone} · {formatDateShort(lead.createdAt)}</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Badge label={lead.retell?.call_status} colors={STATUS_COLORS} />
                        <button onClick={onClose} style={{ ...btnStyle, padding: '6px', borderRadius: '50%' }}><XIcon /></button>
                    </div>
                </div>

                <div style={{ display: 'flex', borderBottom: '1px solid #f1f5f9', padding: '0 24px' }}>
                    {tabs.map(t => (
                        <button key={t.id} onClick={() => setActiveTab(t.id)} style={{ padding: '12px 16px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: activeTab === t.id ? '#1e293b' : '#94a3b8', borderBottom: `2px solid ${activeTab === t.id ? '#1e293b' : 'transparent'}`, transition: 'all 0.15s', marginBottom: -1 }}>
                            {t.label}
                        </button>
                    ))}
                </div>

                <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
                    {activeTab === 'details' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                {[
                                    { icon: <UserIcon />, label: 'Name', value: toTitleCase(lead.name) },
                                    { icon: <PhoneIcon />, label: 'Phone', value: lead.phone },
                                    { icon: <HomeIcon />, label: 'Property', value: toTitleCase(lead.property_interest?.property_name || lead.property_interest?.property_slug) },
                                    { icon: <CalendarIcon />, label: 'Floor Plan', value: toTitleCase(lead.property_interest?.layout_label || lead.property_interest?.preferred_layout) },
                                    { icon: <CalendarIcon />, label: 'Move-In Date', value: lead.property_interest?.move_in_date },
                                    { icon: <MessageIcon />, label: 'City Of Interest', value: toTitleCase(lead.property_interest?.city) },
                                    { icon: <ClockIcon />, label: 'Call Duration', value: formatDuration(lead.retell?.call_duration_ms) },
                                    { icon: <CalendarIcon />, label: 'Appointment', value: lead.appointment?.slot_label },
                                ].map((item, i) => item.value ? (
                                    <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                                        <span style={{ color: '#94a3b8', marginTop: 1, flexShrink: 0 }}>{item.icon}</span>
                                        <div>
                                            <div style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5 }}>{item.label}</div>
                                            <div style={{ fontSize: 13, color: '#1e293b', fontWeight: 500, marginTop: 2 }}>{item.value}</div>
                                        </div>
                                    </div>
                                ) : null)}
                            </div>

                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                {lead.appointment?.booked && <Badge label="appointment_booked" colors={OUTCOME_COLORS} />}
                                {lead.sms?.sent && <Badge label="sms_sent" colors={OUTCOME_COLORS} />}
                            </div>

                            {sourceLog && (
                                <div style={{ background: '#f8fafc', borderRadius: 10, padding: 16, border: '1px solid #e2e8f0' }}>
                                    <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10 }}>
                                        {callLog ? 'Call Log' : 'Chat Log'}
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                                        {[
                                            ['Outcome', <Badge label={sourceLog.outcome} colors={OUTCOME_COLORS} />],
                                            ['Sentiment', <Badge label={sourceLog.sentiment} colors={SENTIMENT_COLORS} />],
                                            callLog && ['Direction', <span style={{ fontSize: 13, color: '#1e293b', textTransform: 'capitalize' }}>{toTitleCase(callLog.call_direction)}</span>],
                                            callLog && ['Duration', <span style={{ fontSize: 13, color: '#1e293b' }}>{formatDuration(callLog.duration_ms)}</span>],
                                            chatLog && ['Agent', <span style={{ fontSize: 13, color: '#1e293b' }}>{chatLog.agent_name || '—'}</span>],
                                        ].filter(Boolean).map(([label, val], i) => val ? (
                                            <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                                <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</span>
                                                {val}
                                            </div>
                                        ) : null)}
                                    </div>
                                    {callLog?.recording_url && (
                                        <div style={{ marginTop: 12 }}>
                                            <audio controls src={callLog.recording_url} style={{ width: '100%', borderRadius: 8, height: 36 }} />
                                        </div>
                                    )}
                                </div>
                            )}

                            <div>
                                <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8 }}>Notes</div>
                                <textarea value={editNotes} onChange={e => setEditNotes(e.target.value)} rows={3} placeholder="Add internal notes..." style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }} />
                                <button onClick={handleSaveNotes} disabled={saving} style={{ ...btnStyle, marginTop: 8, background: '#1e293b', color: '#fff', borderColor: '#1e293b' }}>
                                    {saving ? 'Saving…' : 'Save Notes'}
                                </button>
                            </div>
                        </div>
                    )}

                    {activeTab === 'transcript' && (
                        loadingLog
                            ? <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8', fontSize: 13 }}>Loading Transcript…</div>
                            : <TranscriptViewer transcript={transcriptTurns} />
                    )}

                    {activeTab === 'summary' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                            {summaryText ? (
                                <div style={{ background: '#f8fafc', borderRadius: 10, padding: 16, border: '1px solid #e2e8f0', fontSize: 14, color: '#334155', lineHeight: 1.7 }}>
                                    {summaryText}
                                </div>
                            ) : (
                                <p style={{ color: '#94a3b8', fontSize: 13 }}>No AI Summary Available Yet. Summary Is Generated After Call Analysis Completes.</p>
                            )}
                            {sourceLog && (
                                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                    <Badge label={sourceLog.outcome} colors={OUTCOME_COLORS} />
                                    <Badge label={sourceLog.sentiment} colors={SENTIMENT_COLORS} />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

const LEADS_INIT_FILTERS = { search: '', call_status: '', appointment_booked: '', sms_sent: '', from_date: '', to_date: '', property_slug: '' };

function LeadsTable({ user, propertySlugFilter, globalProperties }) {
    const [leads, setLeads] = useState([]);
    const [pagination, setPagination] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selectedLead, setSelectedLead] = useState(null);
    const [filters, setFilters] = useState(LEADS_INIT_FILTERS);
    const [page, setPage] = useState(1);
    const [showFilters, setShowFilters] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [availableProperties, setAvailableProperties] = useState(globalProperties);

    useEffect(() => {
        api.get('/leads/properties')
            .then(r => {
                const props = r.data.data || [];
                if (props.length > 0) setAvailableProperties(props);
            })
            .catch(() => { });
    }, []);

    const effectiveSlug = propertySlugFilter || filters.property_slug;

    const fetchLeads = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page, limit: 20 };
            if (filters.search) params.search = filters.search;
            if (filters.call_status) params.call_status = filters.call_status;
            if (filters.appointment_booked !== '') params.appointment_booked = filters.appointment_booked;
            if (filters.sms_sent !== '') params.sms_sent = filters.sms_sent;
            if (filters.from_date) params.from_date = filters.from_date;
            if (filters.to_date) params.to_date = filters.to_date;
            if (effectiveSlug) params.property_slug = effectiveSlug;
            const r = await api.get('/leads', params);
            setLeads(r.data.data || []);
            setPagination(r.data.pagination);
        } catch {
            toast.error('Failed To Load Leads');
            setLeads([]);
        } finally {
            setLoading(false);
        }
    }, [page, filters, effectiveSlug]);

    useEffect(() => { fetchLeads(); }, [fetchLeads]);

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this lead permanently?')) return;
        setDeletingId(id);
        try {
            await api.delete(`/leads/${id}`);
            toast.success('Lead Deleted');
            fetchLeads();
        } catch {
            toast.error('Failed To Delete');
        } finally {
            setDeletingId(null);
        }
    };

    const setFilter = (key, val) => {
        setFilters(f => ({ ...f, [key]: val }));
        setPage(1);
    };

    return (
        <div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
                <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
                    <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}><SearchIcon /></span>
                    <input value={filters.search} onChange={e => setFilter('search', e.target.value)} placeholder="Search name, phone, email, property…" style={{ ...inputStyle, paddingLeft: 34 }} />
                </div>
                <button onClick={() => setShowFilters(v => !v)} style={{ ...btnStyle, background: showFilters ? '#1e293b' : '#f8fafc', color: showFilters ? '#fff' : '#374151', borderColor: showFilters ? '#1e293b' : '#e2e8f0' }}>
                    <FilterIcon /> Filters {showFilters ? '▲' : '▼'}
                </button>
                <button onClick={fetchLeads} style={btnStyle}><RefreshIcon /></button>
            </div>

            {showFilters && (
                <div style={{ background: '#f8fafc', borderRadius: 10, padding: 16, marginBottom: 14, border: '1px solid #e2e8f0', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
                    <div>
                        <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>Status</label>
                        <select value={filters.call_status} onChange={e => setFilter('call_status', e.target.value)} style={selectStyle}>
                            <option value="">All</option>
                            {['completed', 'missed', 'failed', 'in_progress', 'voicemail', 'busy', 'no_answer'].map(s => (
                                <option key={s} value={s}>{toTitleCase(s)}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>Appointment</label>
                        <select value={filters.appointment_booked} onChange={e => setFilter('appointment_booked', e.target.value)} style={selectStyle}>
                            <option value="">All</option>
                            <option value="true">Booked</option>
                            <option value="false">Not Booked</option>
                        </select>
                    </div>
                    <div>
                        <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>SMS Sent</label>
                        <select value={filters.sms_sent} onChange={e => setFilter('sms_sent', e.target.value)} style={selectStyle}>
                            <option value="">All</option>
                            <option value="true">Sent</option>
                            <option value="false">Not Sent</option>
                        </select>
                    </div>
                    {!propertySlugFilter && availableProperties.length > 0 && (
                        <div>
                            <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>Property</label>
                            <select value={filters.property_slug} onChange={e => setFilter('property_slug', e.target.value)} style={selectStyle}>
                                <option value="">All Properties</option>
                                {availableProperties.map(p => (
                                    <option key={p.slug} value={p.slug}>{toTitleCase(p.name)}</option>
                                ))}
                            </select>
                        </div>
                    )}
                    <div>
                        <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>From Date</label>
                        <input type="date" value={filters.from_date} onChange={e => setFilter('from_date', e.target.value)} style={inputStyle} />
                    </div>
                    <div>
                        <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>To Date</label>
                        <input type="date" value={filters.to_date} onChange={e => setFilter('to_date', e.target.value)} style={inputStyle} min={filters.from_date || undefined} />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                        <button onClick={() => { setFilters(LEADS_INIT_FILTERS); setPage(1); }} style={{ ...btnStyle, color: '#ef4444', borderColor: '#fecaca' }}>
                            Clear Filters
                        </button>
                    </div>
                </div>
            )}

            {loading ? (
                <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8', fontSize: 13 }}>Loading Leads…</div>
            ) : leads.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8', fontSize: 14 }}>No Leads Found Matching Your Filters.</div>
            ) : (
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                            <tr style={{ borderBottom: '2px solid #f1f5f9' }}>
                                {['Caller', 'Property', 'Status', 'Appointment', 'SMS', 'Date', ''].map((h, i) => (
                                    <th key={i} style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.8, whiteSpace: 'nowrap' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {leads.map(lead => (
                                <tr key={lead._id} style={{ borderBottom: '1px solid #f8fafc', transition: 'background 0.1s' }} onMouseEnter={e => (e.currentTarget.style.background = '#fafafa')} onMouseLeave={e => (e.currentTarget.style.background = '')}>
                                    <td style={{ padding: '12px 12px' }}>
                                        <div style={{ fontWeight: 600, color: '#1e293b' }}>{toTitleCase(lead.name) || <span style={{ color: '#94a3b8' }}>Unknown</span>}</div>
                                        <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{lead.phone}</div>
                                        {lead.email && <div style={{ fontSize: 11, color: '#94a3b8' }}>{lead.email}</div>}
                                    </td>
                                    <td style={{ padding: '12px 12px' }}>
                                        <div style={{ fontWeight: 500, color: '#334155' }}>{toTitleCase(lead.property_interest?.property_name || lead.property_interest?.property_slug) || <span style={{ color: '#cbd5e1' }}>—</span>}</div>
                                        {lead.property_interest?.city && <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{toTitleCase(lead.property_interest?.city)}</div>}
                                    </td>
                                    <td style={{ padding: '12px 12px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <StatusDot status={lead.retell?.call_status} />
                                            <span style={{ fontSize: 12, color: STATUS_COLORS[lead.retell?.call_status]?.text || '#374151', fontWeight: 600 }}>
                                                {toTitleCase(lead.retell?.call_status)}
                                            </span>
                                        </div>
                                    </td>
                                    <td style={{ padding: '12px 12px' }}>
                                        {lead.appointment?.booked
                                            ? <span style={{ background: '#d1fae5', color: '#065f46', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>✓ Booked</span>
                                            : <span style={{ color: '#cbd5e1', fontSize: 12 }}>—</span>}
                                    </td>
                                    <td style={{ padding: '12px 12px' }}>
                                        {lead.sms?.sent
                                            ? <span style={{ background: '#dbeafe', color: '#1e40af', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>✓ Sent</span>
                                            : <span style={{ color: '#cbd5e1', fontSize: 12 }}>—</span>}
                                    </td>
                                    <td style={{ padding: '12px 12px', color: '#64748b', whiteSpace: 'nowrap', fontSize: 12 }}>
                                        {formatDateShort(lead.createdAt)}
                                    </td>
                                    <td style={{ padding: '12px 12px' }}>
                                        <div style={{ display: 'flex', gap: 6 }}>
                                            <button onClick={() => setSelectedLead(lead)} style={{ ...btnStyle, padding: '5px 10px', fontSize: 12 }}>View</button>
                                            {(user?.role === 'company' || user?.role === 'admin') && (
                                                <button onClick={() => handleDelete(lead._id)} disabled={deletingId === lead._id} style={{ ...btnStyle, padding: '5px 10px', fontSize: 12, color: '#ef4444', borderColor: '#fecaca' }}>
                                                    {deletingId === lead._id ? '…' : 'Del'}
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <Pagination pagination={pagination} onPageChange={setPage} />

            {selectedLead && (
                <LeadDetailModal
                    lead={selectedLead}
                    onClose={() => setSelectedLead(null)}
                    onUpdate={() => { fetchLeads(); setSelectedLead(null); }}
                />
            )}
        </div>
    );
}

const LOGS_INIT_FILTERS = { search: '', outcome: '', sentiment: '', call_status: '', call_direction: '', from_date: '', to_date: '' };

function CallLogsTable() {
    const [logs, setLogs] = useState([]);
    const [pagination, setPagination] = useState(null);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState(LOGS_INIT_FILTERS);
    const [page, setPage] = useState(1);
    const [showFilters, setShowFilters] = useState(false);
    const [detailLog, setDetailLog] = useState(null);
    const [loadingDetail, setLoadingDetail] = useState(false);

    const fetchLogs = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page, limit: 20 };
            if (filters.search) params.search = filters.search;
            if (filters.outcome) params.outcome = filters.outcome;
            if (filters.sentiment) params.sentiment = filters.sentiment;
            if (filters.call_status) params.call_status = filters.call_status;
            if (filters.call_direction) params.call_direction = filters.call_direction;
            if (filters.from_date) params.from_date = filters.from_date;
            if (filters.to_date) params.to_date = filters.to_date;
            const r = await api.get('/call-logs', params);
            setLogs(r.data.data || []);
            setPagination(r.data.pagination);
        } catch {
            toast.error('Failed To Load Call Logs');
            setLogs([]);
        } finally {
            setLoading(false);
        }
    }, [page, filters]);

    useEffect(() => { fetchLogs(); }, [fetchLogs]);

    const openDetail = async (log) => {
        setLoadingDetail(true);
        setDetailLog(null);
        try {
            const r = await api.get(`/call-logs/${log._id}`);
            setDetailLog(r.data.data);
        } catch {
            toast.error('Failed To Load Call Details');
        } finally {
            setLoadingDetail(false);
        }
    };

    const setFilter = (key, val) => {
        setFilters(f => ({ ...f, [key]: val }));
        setPage(1);
    };

    return (
        <div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
                <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
                    <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}><SearchIcon /></span>
                    <input value={filters.search} onChange={e => setFilter('search', e.target.value)} placeholder="Search phone, name, summary, transcript…" style={{ ...inputStyle, paddingLeft: 34 }} />
                </div>
                <button onClick={() => setShowFilters(v => !v)} style={{ ...btnStyle, background: showFilters ? '#1e293b' : '#f8fafc', color: showFilters ? '#fff' : '#374151', borderColor: showFilters ? '#1e293b' : '#e2e8f0' }}>
                    <FilterIcon /> Filters {showFilters ? '▲' : '▼'}
                </button>
                <button onClick={fetchLogs} style={btnStyle}><RefreshIcon /></button>
            </div>

            {showFilters && (
                <div style={{ background: '#f8fafc', borderRadius: 10, padding: 16, marginBottom: 14, border: '1px solid #e2e8f0', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 10 }}>
                    {[
                        { key: 'call_status', label: 'Status', options: ['completed', 'missed', 'failed', 'voicemail', 'busy', 'no_answer'] },
                        { key: 'outcome', label: 'Outcome', options: ['appointment_booked', 'sms_sent', 'info_provided', 'not_interested', 'voicemail', 'unknown'] },
                        { key: 'sentiment', label: 'Sentiment', options: ['positive', 'neutral', 'negative'] },
                        { key: 'call_direction', label: 'Direction', options: ['inbound', 'outbound'] },
                    ].map(({ key, label, options }) => (
                        <div key={key}>
                            <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>{label}</label>
                            <select value={filters[key]} onChange={e => setFilter(key, e.target.value)} style={selectStyle}>
                                <option value="">All</option>
                                {options.map(o => <option key={o} value={o}>{toTitleCase(o)}</option>)}
                            </select>
                        </div>
                    ))}
                    <div>
                        <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>From Date</label>
                        <input type="date" value={filters.from_date} onChange={e => setFilter('from_date', e.target.value)} style={inputStyle} />
                    </div>
                    <div>
                        <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>To Date</label>
                        <input type="date" value={filters.to_date} onChange={e => setFilter('to_date', e.target.value)} style={inputStyle} min={filters.from_date || undefined} />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                        <button onClick={() => { setFilters(LOGS_INIT_FILTERS); setPage(1); }} style={{ ...btnStyle, color: '#ef4444', borderColor: '#fecaca' }}>
                            Clear
                        </button>
                    </div>
                </div>
            )}

            {loading ? (
                <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8', fontSize: 13 }}>Loading Call Logs…</div>
            ) : logs.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8', fontSize: 14 }}>No Call Logs Found.</div>
            ) : (
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                            <tr style={{ borderBottom: '2px solid #f1f5f9' }}>
                                {['Caller', 'Direction', 'Status', 'Outcome', 'Sentiment', 'Duration', 'Date', ''].map((h, i) => (
                                    <th key={i} style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.8, whiteSpace: 'nowrap' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {logs.map(log => (
                                <tr key={log._id} style={{ borderBottom: '1px solid #f8fafc', transition: 'background 0.1s' }} onMouseEnter={e => (e.currentTarget.style.background = '#fafafa')} onMouseLeave={e => (e.currentTarget.style.background = '')}>
                                    <td style={{ padding: '12px 12px' }}>
                                        <div style={{ fontWeight: 600, color: '#1e293b' }}>{toTitleCase(log.caller_name) || <span style={{ color: '#94a3b8' }}>Unknown</span>}</div>
                                        <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{log.caller_phone}</div>
                                    </td>
                                    <td style={{ padding: '12px 12px' }}>
                                        <span style={{ fontSize: 12, color: log.call_direction === 'inbound' ? '#1e40af' : '#7c3aed', fontWeight: 600 }}>
                                            {log.call_direction === 'inbound' ? '↙' : '↗'} {toTitleCase(log.call_direction)}
                                        </span>
                                    </td>
                                    <td style={{ padding: '12px 12px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <StatusDot status={log.call_status} />
                                            <span style={{ fontSize: 12, color: STATUS_COLORS[log.call_status]?.text || '#374151', fontWeight: 600 }}>
                                                {toTitleCase(log.call_status)}
                                            </span>
                                        </div>
                                    </td>
                                    <td style={{ padding: '12px 12px' }}><Badge label={log.outcome} colors={OUTCOME_COLORS} /></td>
                                    <td style={{ padding: '12px 12px' }}><Badge label={log.sentiment} colors={SENTIMENT_COLORS} /></td>
                                    <td style={{ padding: '12px 12px', color: '#475569', whiteSpace: 'nowrap', fontSize: 12 }}>{formatDuration(log.duration_ms)}</td>
                                    <td style={{ padding: '12px 12px', color: '#64748b', whiteSpace: 'nowrap', fontSize: 12 }}>{formatDateShort(log.createdAt)}</td>
                                    <td style={{ padding: '12px 12px' }}>
                                        <button onClick={() => openDetail(log)} style={{ ...btnStyle, padding: '5px 10px', fontSize: 12 }}>View</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <Pagination pagination={pagination} onPageChange={setPage} />

            {(detailLog || loadingDetail) && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(4px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={e => { if (e.target === e.currentTarget) setDetailLog(null); }}>
                    <div style={{ background: '#fff', borderRadius: 18, width: '100%', maxWidth: 680, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 50px rgba(0,0,0,0.2)', overflow: 'hidden' }}>
                        {loadingDetail ? (
                            <div style={{ padding: 60, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Loading Call Details…</div>
                        ) : detailLog && (
                            <>
                                <div style={{ padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fafafa' }}>
                                    <div>
                                        <div style={{ fontWeight: 700, fontSize: 16, color: '#1e293b' }}>{toTitleCase(detailLog.caller_name) || detailLog.caller_phone || 'Unknown Caller'}</div>
                                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 2, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                            <span>{detailLog.caller_phone}</span>
                                            <span>·</span>
                                            <span>{formatDate(detailLog.started_at)}</span>
                                            <span>·</span>
                                            <span>{formatDuration(detailLog.duration_ms)}</span>
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                        <Badge label={detailLog.outcome} colors={OUTCOME_COLORS} />
                                        <Badge label={detailLog.sentiment} colors={SENTIMENT_COLORS} />
                                        <button onClick={() => setDetailLog(null)} style={{ ...btnStyle, padding: 6, borderRadius: '50%' }}><XIcon /></button>
                                    </div>
                                </div>
                                <div style={{ flex: 1, overflowY: 'auto', padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
                                    {detailLog.call_summary && (
                                        <div>
                                            <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8 }}>AI Summary</div>
                                            <div style={{ background: '#f8fafc', borderRadius: 10, padding: 16, border: '1px solid #e2e8f0', fontSize: 14, color: '#334155', lineHeight: 1.7 }}>{detailLog.call_summary}</div>
                                        </div>
                                    )}
                                    {detailLog.recording_url && (
                                        <div>
                                            <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8 }}>Recording</div>
                                            <audio controls src={detailLog.recording_url} style={{ width: '100%', borderRadius: 8, height: 36 }} />
                                        </div>
                                    )}
                                    <div>
                                        <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 12 }}>Transcript</div>
                                        <TranscriptViewer transcript={detailLog.transcript} />
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

function StatsPanel() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setLoading(true);
        api.get('/leads/stats')
            .then(r => setStats(r.data.data))
            .catch(() => toast.error('Failed To Load Stats'))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8', fontSize: 13 }}>Loading Analytics…</div>;
    if (!stats) return null;

    const { totals, by_city, by_property, by_outcome } = stats;
    const totalLeads = totals?.total_leads || 0;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
                <StatCard label="Total Leads" value={totalLeads} accent="#1e293b" />
                <StatCard label="Appointments" value={totals?.appointments_booked ?? 0} accent="#10b981" sub={totalLeads ? `${Math.round((totals.appointments_booked / totalLeads) * 100)}% Conversion` : ''} />
                <StatCard label="SMS Sent" value={totals?.sms_sent ?? 0} accent="#3b82f6" />
                <StatCard label="Completed Calls" value={totals?.completed_calls ?? 0} accent="#8b5cf6" />
            </div>

            {by_property?.length > 0 && (
                <div style={cardStyle}>
                    <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', fontWeight: 700, fontSize: 14, color: '#1e293b' }}>Leads By Property</div>
                    <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {by_property.map((p) => {
                            const pct = totalLeads ? Math.round((p.count / totalLeads) * 100) : 0;
                            return (
                                <div key={p._id}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                        <span style={{ fontSize: 13, color: '#334155', fontWeight: 500 }}>{toTitleCase(p.property_name || p._id)}</span>
                                        <span style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>{p.count} <span style={{ fontSize: 11, color: '#94a3b8' }}>({pct}%)</span></span>
                                    </div>
                                    <div style={{ height: 6, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                                        <div style={{ height: '100%', width: `${pct}%`, background: 'linear-gradient(90deg, #3b82f6, #6366f1)', borderRadius: 3, transition: 'width 0.5s' }} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                {by_outcome?.length > 0 && (
                    <div style={cardStyle}>
                        <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', fontWeight: 700, fontSize: 14, color: '#1e293b' }}>Outcomes</div>
                        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {by_outcome.map((o) => (
                                <div key={o._id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Badge label={o._id || 'unknown'} colors={OUTCOME_COLORS} />
                                    <span style={{ fontSize: 14, fontWeight: 700, color: '#1e293b' }}>{o.count}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                {by_city?.length > 0 && (
                    <div style={cardStyle}>
                        <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', fontWeight: 700, fontSize: 14, color: '#1e293b' }}>Top Cities</div>
                        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {by_city.slice(0, 6).map((c) => (
                                <div key={c._id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontSize: 13, color: '#334155', fontWeight: 500 }}>{toTitleCase(c._id)}</span>
                                    <span style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>{c.count}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default function AssistantLeads() {
    const [user, setUser] = useState(null);
    const [activeTab, setActiveTab] = useState('leads');
    const [selectedPropertySlug, setSelectedPropertySlug] = useState('');

    useEffect(() => {
        try {
            const token = localStorage.getItem('accessToken');
            if (token) setUser(jwt_decode(token));
        } catch { }
    }, []);

    const tabs = [
        { id: 'leads', label: 'Leads', icon: <UserIcon /> },
        { id: 'call-logs', label: 'Call Logs', icon: <MicIcon /> },
        { id: 'analytics', label: 'Analytics', icon: <PhoneIcon /> },
    ];

    return (
        <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: "'DM Sans', system-ui, sans-serif" }}>
            <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');`}</style>

            <div style={{ background: '#fff', borderBottom: '1px solid #f1f5f9', padding: '0 28px', display: 'flex', alignItems: 'center', gap: 24, position: 'sticky', top: 0, zIndex: 100 }}>
                <div style={{ padding: '18px 0', display: 'flex', alignItems: 'center', gap: 10, marginRight: 16 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: 'linear-gradient(135deg, #1e293b, #334155)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', flexShrink: 0 }}>
                        <UserIcon />
                    </div>
                    <div>
                        <div style={{ fontWeight: 800, fontSize: 15, color: '#1e293b', lineHeight: 1.2 }}>Assistant Leads</div>
                        <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.8 }}>Omnichannel Dashboard</div>
                    </div>
                </div>

                {tabs.map(t => (
                    <button key={t.id} onClick={() => setActiveTab(t.id)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '18px 4px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: activeTab === t.id ? '#1e293b' : '#94a3b8', borderBottom: `2px solid ${activeTab === t.id ? '#1e293b' : 'transparent'}`, transition: 'all 0.15s', marginBottom: -1, whiteSpace: 'nowrap' }}>
                        {t.icon} {t.label}
                    </button>
                ))}

                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
                    {user && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 12px', background: '#f8fafc', borderRadius: 20, border: '1px solid #e2e8f0' }}>
                            <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#1e293b', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 10, fontWeight: 700 }}>
                                {(user.name || user.email || 'U')[0].toUpperCase()}
                            </div>
                            <span style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>{toTitleCase(user.role)}</span>
                        </div>
                    )}
                </div>
            </div>

            <div style={{ padding: '28px 28px 40px' }}>
                <div style={{ maxWidth: 1200, margin: '0 auto' }}>
                    <div style={cardStyle}>
                        <div style={{ padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div>
                                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#1e293b' }}>
                                    {activeTab === 'leads' ? 'Leads' : activeTab === 'call-logs' ? 'Call Logs' : 'Analytics'}
                                </h2>
                                <p style={{ margin: '4px 0 0', fontSize: 13, color: '#94a3b8' }}>
                                    {activeTab === 'leads'
                                        ? 'Inbound Callers Captured By Your Voice, Chat & WhatsApp Assistants'
                                        : activeTab === 'call-logs'
                                            ? 'Full Interaction Records With Transcripts And AI Analysis'
                                            : 'Performance Overview For Your AI Assistants'}
                                </p>
                            </div>
                            {user?.role && (
                                <span style={{ fontSize: 12, color: '#64748b', background: '#f1f5f9', padding: '4px 10px', borderRadius: 20, fontWeight: 600 }}>
                                    {user.role === 'manager' ? 'Manager View' : 'Company View'}
                                </span>
                            )}
                        </div>
                        <div style={{ padding: 24 }}>
                            {activeTab === 'leads' && (
                                <LeadsTable
                                    user={user}
                                    propertySlugFilter={selectedPropertySlug}
                                    globalProperties={[]}
                                />
                            )}
                            {activeTab === 'call-logs' && <CallLogsTable />}
                            {activeTab === 'analytics' && <StatsPanel />}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}